import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTaskDispute } from '../../../api/backendClient';
import AmountDisplay from '../../../components/amount/AmountDisplay';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { shortenWalletAddress } from '../../../utils/commonFunction';
import { resolveAttachmentUrl } from '../../../utils/attachmentUrl';
import { formatDateTime, formatDueDate, formatStatusTone } from '../formatting';
import { formatTaskStatus, getTaskTransactionHashes, resolveTaskStatus } from '../helpers';
import TaskTransactionHashes from './TaskTransactionHashes';
import { parseTaskDescription } from '../taskDescription';
import {
    getLatestSubmission,
    normalizeSubmissionAttachments,
    splitSubmissionAttachments,
} from '../utils/taskSubmissionUtils';
import { buildTaskLogisticsTimeline } from '../utils/taskLogisticsTimeline';
import TaskActionButtons from './TaskActionButtons';
import TaskDisputeRoom from './TaskDisputeRoom';
import SubmitTaskForm from './SubmitTaskForm';

function formatAmount(value, formatNumberFn, symbol) {
    const num = Number(value) || 0;
    const formatted =
        typeof formatNumberFn === 'function'
            ? formatNumberFn(num)
            : String(num);
    return { formatted, symbol, numeric: num };
}

function relativeSubmittedLabel(submittedAt) {
    if (!submittedAt) return null;
    const date = new Date(submittedAt);
    if (Number.isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 1) return 'Submitted just now';
    if (hours < 24) return `Submitted ${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `Submitted ${days} day${days === 1 ? '' : 's'} ago`;
}

const SummarySectionLabel = ({ icon, children }) => (
    <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
            <i className={`${icon} text-[11px]`} aria-hidden />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider">{children}</span>
    </div>
);

const TaskReviewPage = ({
    task,
    acceptReclaimDelaySeconds,
    tokenSymbol,
    formatNumber,
    normalizedWallet,
    isAdmin,
    isDisputeAdmin = false,
    authToken = '',
    signMessageAsync,
    walletAddress = '',
    activeAction,
    onTaskAction,
    onBack,
    onRefreshTask,
    backLabel = 'Back to tasks',
    showDisputePanel = false,
    openDiscussionOnLoad = false,
    showSubmitForm = false,
    canSubmit = false,
    onCloseSubmitForm,
    onSubmitSuccess,
    onSubmitOnChain,
}) => {
    const submitFormRef = useRef(null);
    const resolvedStatus = resolveTaskStatus(task);
    const taskDetails = parseTaskDescription(task.description);
    const latestSubmission = getLatestSubmission(task);
    const submissionAttachments = normalizeSubmissionAttachments(
        latestSubmission?.attachments,
    );
    const { images: submissionImages, links: submissionLinks } = useMemo(
        () =>
            splitSubmissionAttachments(
                latestSubmission?.attachments,
                latestSubmission,
            ),
        [latestSubmission],
    );
    const amount = formatAmount(task.amount, formatNumber, tokenSymbol);
    const transactionHashes = useMemo(
        () => getTaskTransactionHashes(task),
        [task],
    );

    const employeeName =
        task.employee?.name || shortenWalletAddress(task.employee?.wallet) || 'Unassigned';
    const employeeWallet = task.employee?.wallet;

    const [taskDispute, setTaskDispute] = useState(null);

    const loadTaskDispute = useCallback(async () => {
        if (!authToken || !task?.id) {
            setTaskDispute(null);
            return;
        }
        try {
            const data = await getTaskDispute(authToken, task.id);
            setTaskDispute(data?.dispute || null);
        } catch {
            setTaskDispute(null);
        }
    }, [authToken, task?.id]);

    useEffect(() => {
        loadTaskDispute();
    }, [loadTaskDispute, resolvedStatus]);

    const timeline = useMemo(
        () =>
            buildTaskLogisticsTimeline({
                task,
                latestSubmission,
                resolvedStatus,
                dispute: taskDispute,
            }),
        [task, latestSubmission, resolvedStatus, taskDispute],
    );

    const scopeParagraph = taskDetails.summary || task.description || 'No scope provided.';
    const scopeBullets =
        taskDetails.acceptanceCriteria.length > 0
            ? taskDetails.acceptanceCriteria
            : taskDetails.skills;

    useEffect(() => {
        if (!showSubmitForm) return;
        const timer = window.setTimeout(() => {
            submitFormRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [showSubmitForm]);

    const amountAccent =
        resolvedStatus === 'rejected'
            ? 'from-rose-50/80 to-white dark:from-rose-950/25 dark:to-zinc-950/40'
            : resolvedStatus === 'submitted' || resolvedStatus === 'active'
              ? 'from-teal-50/80 to-white dark:from-teal-950/25 dark:to-zinc-950/40'
              : 'from-zinc-50/80 to-white dark:from-zinc-900/40 dark:to-zinc-950/40';

    return (
        <div className="mx-auto max-w-6xl space-y-5 pb-10">
            <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-lg border border-transparent px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 transition hover:border-zinc-200 hover:bg-zinc-50 hover:text-teal-700 dark:text-zinc-400 dark:hover:border-zinc-700 dark:hover:bg-zinc-900 dark:hover:text-teal-400"
            >
                <i className="fa-solid fa-arrow-left text-[10px]" />
                {backLabel}
            </button>

            <header className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-white via-white to-zinc-50/90 px-5 py-5 shadow-sm dark:border-zinc-800 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900/80 sm:px-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-[1.65rem]">
                                {task.title}
                            </h1>
                            <span className={formatStatusTone(resolvedStatus)}>
                                {formatTaskStatus(resolvedStatus)}
                            </span>
                        </div>
                        <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                            {task.escrowId ? (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 font-mono dark:border-zinc-700 dark:bg-zinc-900">
                                    <i className="fa-solid fa-vault text-[10px] text-teal-600 dark:text-teal-400" />
                                    Escrow #{task.escrowId}
                                </span>
                            ) : null}
                            {task.deadline ? (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900">
                                    <i className="fa-regular fa-calendar text-[10px]" />
                                    {formatDueDate(task.deadline)}
                                </span>
                            ) : null}
                            {taskDetails.category ? (
                                <span className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900">
                                    <i className="fa-solid fa-tag text-[10px] text-zinc-400" />
                                    {taskDetails.category}
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
            </header>

            {showDisputePanel || resolvedStatus === 'disputed' ? (
                <TaskDisputeRoom
                    task={task}
                    authToken={authToken}
                    isDisputeAdmin={isDisputeAdmin}
                    onRefreshTask={onRefreshTask}
                    initialDiscussionOpen={openDiscussionOnLoad}
                />
            ) : null}

            <div className="space-y-5">
                <section className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                    <div className="grid divide-y divide-zinc-100 dark:divide-zinc-800 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.85fr)_minmax(260px,0.95fr)] lg:divide-x lg:divide-y-0">
                        <div className="flex flex-col justify-center p-5 sm:p-6">
                            <SummarySectionLabel icon="fa-solid fa-user-check">
                                Assignee
                            </SummarySectionLabel>
                            <div className="mt-4 flex items-center gap-3">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-zinc-100 to-zinc-50 ring-2 ring-white shadow-sm dark:from-zinc-800 dark:to-zinc-900 dark:ring-zinc-700">
                                    {employeeWallet ? (
                                        <Jazzicon
                                            diameter={48}
                                            seed={jsNumberForAddress(employeeWallet)}
                                        />
                                    ) : (
                                        <i className="fa-regular fa-user text-lg text-zinc-400" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <p className="truncate text-base font-semibold text-zinc-900 dark:text-white">
                                        {employeeName}
                                    </p>
                                    {employeeWallet ? (
                                        <p className="mt-0.5 truncate font-mono text-xs text-zinc-500 dark:text-zinc-400">
                                            {shortenWalletAddress(employeeWallet)}
                                        </p>
                                    ) : (
                                        <p className="mt-0.5 text-xs text-zinc-500">
                                            Wallet not linked
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`flex flex-col justify-center bg-gradient-to-br p-5 sm:p-6 ${amountAccent}`}
                        >
                            <SummarySectionLabel icon="fa-solid fa-coins">
                                Escrow amount
                            </SummarySectionLabel>
                            <AmountDisplay
                                value={amount.numeric}
                                className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white"
                                suffix={amount.symbol}
                                suffixClassName="ml-2 text-base font-semibold text-teal-700 dark:text-teal-400"
                            />
                            <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                                Locked until release or dispute resolution
                            </p>
                        </div>

                        <div className="flex flex-col justify-center bg-zinc-50/50 p-5 dark:bg-zinc-900/30 sm:p-6">
                            <SummarySectionLabel icon="fa-solid fa-bolt">
                                Actions
                            </SummarySectionLabel>
                            <div className="mt-4">
                                <TaskActionButtons
                                    task={task}
                                    normalizedWallet={normalizedWallet}
                                    isAdmin={isAdmin}
                                    activeAction={activeAction}
                                    onTaskAction={onTaskAction}
                                    layout="page"
                                    showSubmitForm={showSubmitForm}
                                    acceptReclaimDelaySeconds={acceptReclaimDelaySeconds}
                                />
                            </div>
                            <p className="mt-3 flex items-start gap-1.5 text-[10px] leading-snug text-zinc-500 dark:text-zinc-500">
                                <i className="fa-solid fa-circle-info mt-0.5 text-[9px] text-zinc-400" />
                                On-chain actions are irreversible once confirmed.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/40 sm:p-6">
                    <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-400">
                            <i className="fa-solid fa-list-check text-sm" />
                        </span>
                        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                            Scope of work
                        </h2>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap dark:text-zinc-300">
                        {scopeParagraph}
                    </p>
                    {scopeBullets.length > 0 ? (
                        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
                            {scopeBullets.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        ) : null}
                </section>

                {showSubmitForm ? (
                    <div ref={submitFormRef}>
                        <SubmitTaskForm
                            task={task}
                            canSubmit={canSubmit}
                            backendToken={authToken}
                            signMessageAsync={signMessageAsync}
                            walletAddress={walletAddress}
                            onCancel={onCloseSubmitForm}
                            onSubmitSuccess={onSubmitSuccess}
                            onSubmitOnChain={onSubmitOnChain}
                        />
                    </div>
                ) : null}

                {latestSubmission ? (
                    <section className="rounded-xl border border-zinc-200 bg-zinc-50/90 p-5 dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-zinc-500">
                                <i className="fa-regular fa-envelope text-sm" />
                                <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                                    Submission details
                                </h2>
                            </div>
                            {relativeSubmittedLabel(latestSubmission.submittedAt) ? (
                                <p className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                                    <i className="fa-regular fa-clock" />
                                    {relativeSubmittedLabel(latestSubmission.submittedAt)}
                                </p>
                            ) : null}
                        </div>
                        <blockquote className="mt-3 border-l-2 border-teal-500/50 pl-3 text-sm italic leading-relaxed text-zinc-700 dark:text-zinc-300">
                            {latestSubmission.message || 'No message provided.'}
                        </blockquote>
                        {latestSubmission._decryptionError ? (
                            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                                {latestSubmission._decryptionError}
                            </p>
                        ) : null}
                        {submissionLinks.length > 0 ? (
                            <div className="mt-4 space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                    Proof links
                                </p>
                                <ul className="space-y-2">
                                    {submissionLinks.map((attachment, i) => {
                                        const url = resolveAttachmentUrl(attachment);
                                        return (
                                            <li key={`link-${i}-${url}`}>
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex max-w-full items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-800 transition hover:border-teal-300 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200"
                                                >
                                                    <i className="fa-solid fa-link text-[10px] text-teal-600 dark:text-teal-400" />
                                                    <span className="min-w-0 truncate">{url}</span>
                                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-zinc-400" />
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : null}
                        {submissionImages.length > 0 ? (
                            <div className="mt-4 space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                                    Proof images
                                </p>
                                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                                    {submissionImages.map((attachment, i) => {
                                        const url = resolveAttachmentUrl(attachment);
                                        const isBlobUrl = String(url || '')
                                            .toLowerCase()
                                            .startsWith('blob:');
                                        const imageEl = (
                                            <img
                                                src={url}
                                                alt={`Submission image ${i + 1}`}
                                                className="aspect-square w-full object-cover"
                                            />
                                        );
                                        return (
                                            <li key={`img-${i}-${url}`}>
                                                {isBlobUrl ? (
                                                    <div className="block overflow-hidden rounded-lg border border-zinc-200 bg-white ring-1 ring-zinc-200/80 dark:border-zinc-700 dark:bg-zinc-950 dark:ring-zinc-700">
                                                        {imageEl}
                                                    </div>
                                                ) : (
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="block overflow-hidden rounded-lg border border-zinc-200 bg-white ring-1 ring-zinc-200/80 transition hover:ring-teal-400 dark:border-zinc-700 dark:bg-zinc-950 dark:ring-zinc-700 dark:hover:ring-teal-500/50"
                                                    >
                                                        {imageEl}
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : null}
                    </section>
                ) : (
                    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 px-5 py-4 text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/30">
                        No submission yet.
                    </div>
                )}

                <TaskTransactionHashes
                    entries={transactionHashes}
                    escrowId={task.escrowId}
                />

                <section className="rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-950/40">
                    <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                        Logistics log
                    </h2>
                    <ol className="mt-4 space-y-0">
                        {timeline.map((step, index) => {
                            const isLast = index === timeline.length - 1;
                            return (
                                <li key={step.id} className="relative flex gap-4 pb-6 last:pb-0">
                                    {!isLast ? (
                                        <span
                                            className="absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-px bg-zinc-200 dark:bg-zinc-800"
                                            aria-hidden
                                        />
                                    ) : null}
                                    <span
                                        className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-xs ${
                                            step.current
                                                ? step.id?.startsWith('dispute')
                                                    ? 'border-amber-500 bg-amber-50 text-amber-800 dark:border-amber-500 dark:bg-amber-950/50 dark:text-amber-300'
                                                    : 'border-teal-500 bg-teal-50 text-teal-700 dark:border-teal-500 dark:bg-teal-950/50 dark:text-teal-400'
                                                : step.done
                                                  ? step.id?.startsWith('dispute')
                                                    ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                                    : 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400'
                                                  : 'border-zinc-200 bg-white text-zinc-400 dark:border-zinc-800 dark:bg-zinc-950'
                                        }`}
                                    >
                                        <i className={step.icon} />
                                    </span>
                                    <div className="min-w-0 pt-0.5">
                                        <p className="text-sm font-medium text-zinc-900 dark:text-white">
                                            {step.label}
                                        </p>
                                        <p className="mt-0.5 text-xs text-zinc-500">
                                            {step.date
                                                ? formatDateTime(step.date)
                                                : 'Pending'}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </section>
            </div>
        </div>
    );
};

export default TaskReviewPage;
